import React, { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import {
  FileText,
  Calendar,
  Clock,
  Eye,
  RotateCcw,
  Plus,
  CheckCircle,
  XCircle,
  Users,
} from "lucide-react";
import dayjs, { Dayjs } from "dayjs";
import {
  useLazyGetCalendarDataQuery,
  useLazyGetTodayStatusByDateQuery,
  IBreak,
} from "../../store/apis/attendance.api";
import {
  ConfirmationModal,
  DatePicker,
  Select,
  SimpleTooltip,
  DataTable,
  ColumnSelector,
  useTableConfig,
  PageHeader,
  StatsCards,
  Button,
  IconActionButton,
} from "../common";
import Modal2, { Modal2Footer, Modal2Button } from "../common/Modal2";
import { TableColumn } from "../common/Table";
import {
  getWorkflowStatusColor,
  getWorkflowStatusFallbackLabel,
  isPendingWorkflowStatus,
  isApprovedWorkflowStatus,
  isRejectedWorkflowStatus,
  WorkflowStatusCode,
} from "../../utils/constants";
import { useTimezone } from "../../hooks/useTimezone";
import { RegularizationTableSkeleton } from "./Skeleton";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import Badge from "../common/Badge";
import { getWorkflowStatusVariant } from "../../utils/badgeVariants";
import { getWorkflowStatusIcon } from "./regularization-management/tableColumns";
import { TextArea } from "../common/TextArea";
import { useAuth } from "../../store/hooks/useAuth";
import {
  ICreateRegularizationPayload,
  IRegularizationRequest,
  useCreateRegularizationMutation,
  useGetMyRegularizationRequestsQuery,
  usePullbackRegularizationMutation,
} from "../../store/apis/attendanceRegularization.api";
import { IMasterConfigOption, useGetMasterConfigByCategoryQuery } from "../../store/apis/masterConfig.api";

/** Parse YYYY-MM-DD to { month: 1-12, year } for v2 calendar API */
function getMonthYearFromDate(dateStr: string): { month: number; year: number } {
  const [yearStr, monthStr] = dateStr.split("-");
  const year = parseInt(yearStr!, 10);
  const month = parseInt(monthStr!, 10);
  return { month, year };
}

/** Parse 12h time "9:58 AM" / "10:18 PM" to 24h { hours, minutes } */
function parse12hTime(str: string): { hours: number; minutes: number } | null {
  const match = String(str).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const ampm = match[3].toUpperCase();
  if (ampm === "PM" && hours !== 12) hours += 12;
  if (ampm === "AM" && hours === 12) hours = 0;
  return { hours, minutes };
}

/** Build UTC ISO for given time on dateStr (YYYY-MM-DD) */
function utcTimeOnDateToISO(dateStr: string, hours: number, minutes: number): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dateStr}T${pad(hours)}:${pad(minutes)}:00.000Z`;
}

/**
 * Replace UTC times in backend AR error message with IST for display.
 * Backend sends break times in UTC (e.g. "09:58 AM - 10:18 AM"); we show them in IST.
 */
function convertBreakTimesInMessageToIST(
  message: string,
  dateStr: string,
  formatTime: (iso: string) => string
): string {
  if (!message || !dateStr) return message;
  try {
    // Pattern 1: (09:58 AM - 10:18 AM)
    let out = message.replace(
      /\((\d{1,2}:\d{2}\s*[AP]M)\s*-\s*(\d{1,2}:\d{2}\s*[AP]M)\)/gi,
      (_, t1, t2) => {
        const p1 = parse12hTime(t1);
        const p2 = parse12hTime(t2);
        if (!p1 || !p2) return _;
        const iso1 = utcTimeOnDateToISO(dateStr, p1.hours, p1.minutes);
        const iso2 = utcTimeOnDateToISO(dateStr, p2.hours, p2.minutes);
        return `(${formatTime(iso1)} - ${formatTime(iso2)})`;
      }
    );
    // Pattern 2: (started at 09:58 AM)
    out = out.replace(
      /\(started at (\d{1,2}:\d{2}\s*[AP]M)\)/gi,
      (_, t) => {
        const p = parse12hTime(t);
        if (!p) return _;
        const iso = utcTimeOnDateToISO(dateStr, p.hours, p.minutes);
        return `(started at ${formatTime(iso)})`;
      }
    );
    return out;
  } catch {
    return message;
  }
}

const AttendanceRegularization: React.FC = () => {
  useAuth();
  const canManage = useHasPermission(PERMISSIONS.APPLY_AR_MANAGE);
  const { formatDate, formatTime, toUTC, todayIST, dateToUTC } = useTimezone();
  const maxRegularizationDate = dayjs(todayIST());

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Form state
  const [formData, setFormData] = useState<ICreateRegularizationPayload>({
    date: "",
    regularizationType: "",
    requestedCheckInTime: "09:00",
    requestedCheckOutTime: "18:00",
    reason: "",
  });

  // Modal state
  const [selectedRequest, setSelectedRequest] = useState<IRegularizationRequest | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<IRegularizationRequest | null>(null);

  // Attendance data for form (when date is selected)
  const [formAttendanceData, setFormAttendanceData] = useState<{
    checkInTime?: string;
    checkOutTime?: string;
    breaks?: IBreak[];
  } | null>(null);
  const [loadingFormAttendance, setLoadingFormAttendance] = useState(false);

  // Attendance data for viewing details
  const [attendanceData, setAttendanceData] = useState<{
    checkInTime?: string;
    checkOutTime?: string;
    breaks?: IBreak[];
  } | null>(null);
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  // RTK Query hooks
  const {
    data: regularizationTypesData,
    isLoading: loadingRegularizationTypes,
  } = useGetMasterConfigByCategoryQuery("attendance_regularization_reason");

  const {
    data: myRequestsData,
    isLoading: loadingMyRequests,
    refetch: refetchMyRequests,
  } = useGetMyRegularizationRequestsQuery({
    page: currentPage,
    limit: pageSize,
  });

  const [getTodayStatusByDate] = useLazyGetTodayStatusByDateQuery();

  const [createRegularization, { isLoading: submitting }] = useCreateRegularizationMutation();
  const [pullbackRegularization, { isLoading: pullbackLoading }] = usePullbackRegularizationMutation();
  const [fetchCalendarData] = useLazyGetCalendarDataQuery();


  // Extract data from RTK Query response
  const requests = myRequestsData?.data || [];
  const totalRequests = myRequestsData?.total || 0;
  const totalPages = myRequestsData?.totalPages || 1;

  const stats = useMemo(() => ({
    total: totalRequests,
    pending: requests.filter((r) => isPendingWorkflowStatus(r.status as WorkflowStatusCode)).length,
    approved: requests.filter((r) => isApprovedWorkflowStatus(r.status as WorkflowStatusCode)).length,
    rejected: requests.filter((r) => isRejectedWorkflowStatus(r.status as WorkflowStatusCode)).length,
  }), [requests, totalRequests]);

  // Fetch attendance for the selected date via v2 calendar (month containing that date)
  useEffect(() => {
    const fetchFormAttendance = async () => {
      if (!formData.date) {
        setFormAttendanceData(null);
        return;
      }

      try {
        setLoadingFormAttendance(true);
        const dateStr = formData.date;
        const result = await getTodayStatusByDate(dateStr).unwrap().catch(() => null);
        const dayForDate = result?.data;

        if (dayForDate && (dayForDate.checkInTime || dayForDate.checkOutTime)) {
          setFormAttendanceData({
            checkInTime: dayForDate.checkInTime ?? undefined,
            checkOutTime: dayForDate.checkOutTime ?? undefined,
            breaks: dayForDate.breaks ?? [],
          });
        } else {
          setFormAttendanceData(null);
        }
      } catch (error) {
        console.error("Error fetching attendance:", error);
        setFormAttendanceData(null);
      } finally {
        setLoadingFormAttendance(false);
      }
    };

    fetchFormAttendance();
  }, [formData.date, getTodayStatusByDate]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (value: Dayjs | null) => {
    setFormData((prev) => ({
      ...prev,
      date: value ? value.format("YYYY-MM-DD") : "",
    }));
  };

  const handleRegularizationTypeChange = (
    value: string | number | (string | number)[]
  ) => {
    if (Array.isArray(value)) return;
    setFormData((prev) => ({
      ...prev,
      regularizationType: String(value),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.regularizationType) {
      toast.error("Please select a regularization type");
      return;
    }

    try {
      // Convert IST to UTC before sending to backend
      const payload: ICreateRegularizationPayload = {
        date: dateToUTC(formData.date),
        regularizationType: formData.regularizationType,
        requestedCheckInTime: toUTC(formData.date, formData.requestedCheckInTime),
        requestedCheckOutTime: toUTC(formData.date, formData.requestedCheckOutTime),
        reason: formData.reason,
      };

      await createRegularization(payload).unwrap();
      toast.success("Regularization request submitted successfully!");
      setShowFormModal(false);

      // Reset form
      setFormData({
        date: "",
        regularizationType: "",
        requestedCheckInTime: "09:00",
        requestedCheckOutTime: "18:00",
        reason: "",
      });

      // Refetch requests
      refetchMyRequests();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string }, message?: string };
      const rawMessage = error?.data?.message || error?.message || "Failed to submit regularization request";
      const displayMessage = formData.date
        ? convertBreakTimesInMessageToIST(rawMessage, formData.date, formatTime)
        : rawMessage;
      toast.error(displayMessage);
    }
  };

  const handleViewDetails = async (request: IRegularizationRequest) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
    setAttendanceData(null);

    // Fetch attendance for the request date via v2 calendar (month containing that date)
    if (request.date) {
      try {
        setLoadingAttendance(true);
        const dateStr = request.date.split("T")[0];
        const { month, year } = getMonthYearFromDate(dateStr);
        const result = await fetchCalendarData({ month, year }).unwrap().catch(() => null);
        const days = result?.data?.days as Array<{ date: string; checkInTime?: string; checkOutTime?: string }> | undefined;
        const dayForDate = Array.isArray(days)
          ? days.find((d) => (d.date && String(d.date).split("T")[0]) === dateStr)
          : undefined;

        if (dayForDate && (dayForDate.checkInTime || dayForDate.checkOutTime)) {
          setAttendanceData({
            checkInTime: dayForDate.checkInTime ?? undefined,
            checkOutTime: dayForDate.checkOutTime ?? undefined,
          });
        } else {
          setAttendanceData(null);
        }
      } catch (error) {
        console.error("Error fetching attendance:", error);
        setAttendanceData(null);
      } finally {
        setLoadingAttendance(false);
      }
    }
  };

  const getStatusColor = (status: WorkflowStatusCode) =>
    getWorkflowStatusColor(status);

  const canPullback = (req: IRegularizationRequest): boolean =>
    isPendingWorkflowStatus(req.status as WorkflowStatusCode);

  const onPullbackClick = (req: IRegularizationRequest) => {
    setConfirmTarget(req);
    setConfirmOpen(true);
  };

  const confirmPullback = async () => {
    if (!confirmTarget) return;
    try {
      await pullbackRegularization(confirmTarget.id).unwrap();
      toast.success("Regularization pulled back successfully");
      refetchMyRequests();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string }, message?: string };
      toast.error(error?.data?.message || error.message || "Failed to pull back");
    } finally {
      setConfirmOpen(false);
      setConfirmTarget(null);
    }
  };

  const regularizationTypeOptions = React.useMemo(() => {
    if (!regularizationTypesData || !Array.isArray(regularizationTypesData)) {
      return [];
    }

    return regularizationTypesData.map((type: IMasterConfigOption) => ({
      value: type.id,
      label: type.displayName,
    }));
  }, [regularizationTypesData]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleCloseFormModal = () => {
    setShowFormModal(false);
    setFormData({
      date: "",
      regularizationType: "",
      requestedCheckInTime: "09:00",
      requestedCheckOutTime: "18:00",
      reason: "",
    });
    setFormAttendanceData(null);
  };

  const attendanceColumns: TableColumn<IRegularizationRequest>[] = useMemo(() => [
    {
      key: "dateType",
      title: "DATE & TYPE",
      label: "Date & Type",
      required: true,
      render: (_, request) => (
        <div className="whitespace-nowrap">
          <div className="flex items-center text-sm font-medium text-[var(--color-text-primary)]">
            <Calendar className="w-4 h-4 mr-1 text-[var(--color-text-tertiary)]" />
            {formatDate(request.date)}
          </div>
          <div className="text-sm text-[var(--color-text-tertiary)] mt-1">
            {request.regularizationTypeName}
          </div>
        </div>
      ),
    },
    {
      key: "requestedTime",
      title: "REQUESTED TIME",
      label: "Requested Time",
      render: (_, request) => (
        <div className="text-sm text-[var(--color-text-primary)] whitespace-nowrap">
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-1 text-[var(--color-text-tertiary)]" />
            {formatTime(request.requestedCheckInTime)} -{" "}
            {formatTime(request.requestedCheckOutTime)}
          </div>
        </div>
      ),
    },
    {
      key: "reason",
      title: "REASON",
      label: "Reason",
      render: (_, request) => (
        <div className="flex items-center w-[220px]">
          <SimpleTooltip label={request.reason || "—"} side="top">
            <span className="text-sm text-[var(--color-text-primary)] block max-w-[220px] truncate cursor-pointer">
              {request.reason || "—"}
            </span>
          </SimpleTooltip>
        </div>
      ),
    },
    {
      key: "status",
      title: "STATUS",
      label: "Status",
      render: (_, request) => (
        <div>
          <Badge
            variant={getWorkflowStatusVariant(request.status as WorkflowStatusCode)}
          >
            {(() => {
              const Icon = getWorkflowStatusIcon(request.status as WorkflowStatusCode);
              return <Icon className="w-4 h-4" />;
            })()}
            {request.statusLabel ||
              getWorkflowStatusFallbackLabel(
                request.status as WorkflowStatusCode,
                request.currentActorName
              )}
          </Badge>
          {isApprovedWorkflowStatus(request.status as WorkflowStatusCode) &&
            request.approvedByName && (
              <div className="text-xs text-[var(--color-text-tertiary)] mt-1">
                by {request.approvedByName}
              </div>
            )}
        </div>
      ),
    },
    {
      key: "createdAt",
      title: "APPLIED DATE",
      label: "Applied Date",
      render: (_, request) => (
        <span className="text-sm text-[var(--color-text-tertiary)] whitespace-nowrap">{formatDate(request.createdAt)}</span>
      ),
    },
    {
      key: "actions",
      title: "ACTIONS",
      label: "Actions",
      align: "right",
      required: true,
      render: (_, request) => (
        <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
          <SimpleTooltip
            label="View"
            side="top"
            className="inline-block"
            tooltipClassName="text-xs shadow-soft border-0"
          >
            <IconActionButton title="View" onClick={() => handleViewDetails(request)}>
              <Eye className="w-4 h-4" />
            </IconActionButton>
          </SimpleTooltip>

          {canManage && canPullback(request) && (
            <SimpleTooltip
              label="Pull back"
              side="top"
              className="inline-block"
              tooltipClassName="text-xs shadow-soft border-0"
            >
              <IconActionButton title="Pull back" onClick={() => onPullbackClick(request)}>
                <RotateCcw className="w-4 h-4" />
              </IconActionButton>
            </SimpleTooltip>
          )}
        </div>
      ),
    },
  ], [canManage, formatDate, formatTime]);

  const { configuredColumns, visibleKeys, toggleVisibility, allColumns } =
    useTableConfig(attendanceColumns, {
      persistenceKey: "attendance-regularization-my-requests",
    });

  const renderFormAttendance = () => {
    if (loadingFormAttendance) {
      return (
        <div className="flex items-center gap-2 text-sm text-[var(--color-text-tertiary)]">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--color-primary-600)]" />
          <span>Loading attendance data for {formatDate(formData.date)}...</span>
        </div>
      );
    }

    if (!formAttendanceData) {
      return (
        <div className="text-sm text-[var(--color-text-tertiary)] italic">
          No attendance record found for {formatDate(formData.date)}
        </div>
      );
    }

    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-[11px] border border-[var(--color-border-primary)] bg-[var(--color-bg-base)] p-3">
            <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
              Actual Check-in Time
            </p>
            <p className="text-sm font-semibold text-[var(--color-text-primary)] flex items-center gap-1">
              <Clock className="w-4 h-4 text-emerald-600" />
              {formAttendanceData.checkInTime
                ? formatTime(formAttendanceData.checkInTime)
                : "N/A"}
            </p>
          </div>
          <div className="rounded-[11px] border border-[var(--color-border-primary)] bg-[var(--color-bg-base)] p-3">
            <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
              Actual Check-out Time
            </p>
            <p className="text-sm font-semibold text-[var(--color-text-primary)] flex items-center gap-1">
              <Clock className="w-4 h-4 text-red-600" />
              {formAttendanceData.checkOutTime
                ? formatTime(formAttendanceData.checkOutTime)
                : "N/A"}
            </p>
          </div>
        </div>
        {formAttendanceData.breaks && formAttendanceData.breaks.length > 0 && (
          <div>
            <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-2">
              Breaks
            </p>
            <div className="space-y-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {formAttendanceData.breaks.map((brk, index) => (
                <div
                  key={index}
                  className="rounded-[11px] border border-amber-200 bg-amber-50 px-3 py-2 text-sm flex items-center justify-between !mt-0"
                >
                  <div>
                    <p className="text-xs font-semibold text-amber-800 capitalize">{brk?.type || "Break"}</p>
                    <p className="text-xs text-amber-700 flex items-center gap-1 mt-0.5">
                      <Clock className="w-3.5 h-3.5" />
                      {formatTime(brk?.startTime) || "N/A"} - {formatTime(brk?.endTime) || "N/A"}
                    </p>
                  </div>
                  <span className="text-xs text-amber-600">{(brk.duration ?? 0).toFixed(2)} mins</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="w-full">

      <PageHeader
        title="Attendance Regularization"
        description="Regularize your attendance records."
        breadcrumbs={[{ label: "Home", path: "/dashboard" }, { label: "Attendance Regularization" }]}
        actions={canManage ? (
          <Button
            appearance="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setShowFormModal(true)}
          >
            New Request
          </Button>
        ) : undefined}
      />

      {/* ── Stat Cards ── */}
      <div className="mb-5 sm:mb-6">
        <StatsCards
          stats={stats}
          cards={[
            { key: "total", label: "Total Requests", icon: Users, iconBg: "var(--color-primary-100)", iconColor: "var(--color-primary-600)" },
            { key: "pending", label: "Pending", icon: Clock, iconBg: "var(--color-warning-light)", iconColor: "var(--color-warning-dark)" },
            { key: "approved", label: "Approved (YTD)", icon: CheckCircle, iconBg: "var(--color-success-light)", iconColor: "var(--color-success)" },
            { key: "rejected", label: "Rejected (YTD)", icon: XCircle, iconBg: "var(--color-error-light)", iconColor: "var(--color-error)" },
          ]}
          variant="simple-icon"
          gridCols="lg:grid-cols-4"
          valueOnTop
          cardClassName="rounded-[18px] bg-[var(--color-bg-base)] border border-[var(--color-border-secondary)] shadow-[var(--shadow-sm)] p-[18px] sm:p-6 transition-all duration-150 hover:-translate-y-[3px] hover:shadow-[var(--shadow-md)]"
          containerClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
        />
      </div>

      {/* ── New Request Modal ── */}
      <Modal2
        isOpen={showFormModal}
        onClose={handleCloseFormModal}
        title="Apply for Regularization"
        size="md"
      >
        <p className="text-sm text-[var(--color-text-secondary)] mb-4">
          Request correction for your attendance records.
        </p>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DatePicker
              label="Date"
              required
              value={formData.date ? dayjs(formData.date) : null}
              onChange={handleDateChange}
              maxDate={maxRegularizationDate}
              placeholder="Select date"
              format="DD/MM/YYYY"
              allowClear={false}
            />
            <Select
              label="Regularization Type"
              required
              value={formData.regularizationType}
              onChange={handleRegularizationTypeChange}
              options={regularizationTypeOptions}
              loading={loadingRegularizationTypes}
              placeholder="Select regularization type"
              searchable={false}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[var(--color-text-primary)] mb-[7px]">
                Requested Check-in Time <span className="text-[var(--color-error)] ml-1">*</span>
              </label>
              <input
                type="time"
                name="requestedCheckInTime"
                value={formData.requestedCheckInTime}
                onChange={handleInputChange}
                className="w-full h-[44px] px-[14px] rounded-[11px] border border-[var(--color-border-primary)] bg-[var(--color-bg-muted)] text-sm text-[var(--color-text-primary)] hover:border-[var(--color-border-input-hover)] focus:outline-none focus:border-[var(--color-border-focus)] focus:shadow-[0_0_0_3px_rgba(109,40,217,0.14)] transition-all duration-200"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--color-text-primary)] mb-[7px]">
                Requested Check-out Time <span className="text-[var(--color-error)] ml-1">*</span>
              </label>
              <input
                type="time"
                name="requestedCheckOutTime"
                value={formData.requestedCheckOutTime}
                onChange={handleInputChange}
                className="w-full h-[44px] px-[14px] rounded-[11px] border border-[var(--color-border-primary)] bg-[var(--color-bg-muted)] text-sm text-[var(--color-text-primary)] hover:border-[var(--color-border-input-hover)] focus:outline-none focus:border-[var(--color-border-focus)] focus:shadow-[0_0_0_3px_rgba(109,40,217,0.14)] transition-all duration-200"
                required
              />
            </div>
          </div>

          <TextArea
            label="Reason"
            value={formData.reason ?? ""}
            onChange={(value: string) => setFormData({ ...formData, reason: value })}
            placeholder="Please provide a detailed reason..."
            required
          />

          {/* Actual attendance for selected date */}
          {formData.date && (
            <div className="pt-3 border-t border-[var(--color-border-primary)] space-y-3">
              <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em]">
                Current Attendance
              </p>
              {renderFormAttendance()}
            </div>
          )}

          <Modal2Footer>
            <Modal2Button variant="secondary" onClick={handleCloseFormModal} disabled={submitting}>
              Cancel
            </Modal2Button>
            <Modal2Button variant="primary" type="submit" loading={submitting} disabled={submitting}>
              {submitting ? "Submitting..." : "Submit Request"}
            </Modal2Button>
          </Modal2Footer>
        </form>
      </Modal2>

      {/* ── My Regularization Requests ── */}
      {loadingMyRequests ? (
        <RegularizationTableSkeleton />
      ) : (
        <div className="bg-white border border-[var(--color-border-primary)] rounded-[18px] overflow-hidden shadow-[var(--shadow-sm)]">
          <div className="px-[18px] sm:px-[22px] py-[14px] sm:py-[18px] border-b border-[var(--color-border-primary)] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-[var(--color-text-primary)]">
                My Regularization Requests
              </h2>
              <span className="text-xs font-semibold text-[var(--color-text-tertiary)]">
                {totalRequests} total
              </span>
            </div>
            <ColumnSelector
              columns={allColumns}
              visibleKeys={visibleKeys}
              onToggle={toggleVisibility}
            />
          </div>
          <DataTable<IRegularizationRequest>
            columns={configuredColumns}
            rows={requests}
            rowKey={(req) => req.id}
            loading={loadingMyRequests}
            emptyMessage="No regularization requests found"
            responsive="auto"
            pagination={{
              page: currentPage,
              pageSize: pageSize,
              total: totalRequests,
              onPageChange: handlePageChange,
              onPageSizeChange: (newLimit) => {
                setPageSize(newLimit);
                setCurrentPage(1);
              },
            }}
          />
        </div>
      )}

      {/* ── Details Modal ── */}
      {selectedRequest && (
        <Modal2
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          title="Regularization Request Details"
          size="lg"
          footer={
            <Modal2Footer justify="end">
              <Modal2Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
                Close
              </Modal2Button>
            </Modal2Footer>
          }
        >
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                  Employee
                </p>
                <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                  {selectedRequest.fullName} ({selectedRequest.employeeId})
                </p>
              </div>
              <div>
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                  Date
                </p>
                <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                  {formatDate(selectedRequest.date)}
                </p>
              </div>
              <div>
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                  Type
                </p>
                <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                  {selectedRequest.regularizationTypeName}
                </p>
              </div>
              <div>
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                  Requested Time
                </p>
                <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                  {formatTime(selectedRequest.requestedCheckInTime)} -{" "}
                  {formatTime(selectedRequest.requestedCheckOutTime)}
                </p>
              </div>
              <div>
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                  Actual Time
                </p>
                {loadingAttendance ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-[var(--color-primary-600)]" />
                    <span className="text-xs text-[var(--color-text-tertiary)]">Loading...</span>
                  </div>
                ) : (
                  <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                    {attendanceData ? (
                      <>{formatTime(attendanceData.checkInTime)} - {formatTime(attendanceData.checkOutTime)}</>
                    ) : (
                      <span className="italic text-[var(--color-text-tertiary)]">Not available</span>
                    )}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                  Status
                </p>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedRequest.status as WorkflowStatusCode)}`}>
                  {selectedRequest.statusLabel ||
                    getWorkflowStatusFallbackLabel(selectedRequest.status as WorkflowStatusCode, selectedRequest.currentActorName)}
                </span>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                Reason
              </p>
              <p className="text-sm text-[var(--color-text-primary)]">{selectedRequest.reason}</p>
            </div>

            {selectedRequest.approvedByName && (
              <div>
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                  {isApprovedWorkflowStatus(selectedRequest.status as WorkflowStatusCode)
                    ? "Approved By"
                    : isRejectedWorkflowStatus(selectedRequest.status as WorkflowStatusCode)
                      ? "Rejected By"
                      : "Processed By"}
                </p>
                <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                  {selectedRequest.approvedByName}
                </p>
              </div>
            )}
            {selectedRequest.rejectionReason && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-[11px]">
                <p className="text-xs font-bold text-red-700 uppercase tracking-[0.05em] mb-1">
                  Rejection Reason
                </p>
                <p className="text-sm text-red-700">{selectedRequest.rejectionReason}</p>
              </div>
            )}
          </div>
        </Modal2>
      )}

      {/* ── Pullback Confirmation Modal ── */}
      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmPullback}
        title="Pull back regularization request"
        message={
          confirmTarget
            ? `Are you sure you want to pull-back your request for ${formatDate(confirmTarget.date)} (${confirmTarget.regularizationTypeName})?`
            : ""
        }
        type="warning"
        confirmText={pullbackLoading ? "Pulling back..." : "Pull back"}
        isLoading={pullbackLoading}
      />
    </div>
  );
};

export default AttendanceRegularization;
